
import axios from 'axios';
import * as turf from "@turf/turf";
import { useState, useRef, useEffect, useCallback } from "react"
import Select from 'react-select'
import Table from 'react-bootstrap/Table';
import { formatDate } from '../CustomDateTools';
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    Popup,

    Source,
    Layer,
    useMap
} from 'react-map-gl/maplibre';




const GoldFinderGeoserver = axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER
})

// gswa_load_date arrives from WFS as an ISO string (or null/undefined for
// undated samples). Render it as DD/MM/YYYY, or a dash when absent/invalid.
function formatLoadDate(value) {
    if (!value) return "—";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "—" : formatDate(d);
}



/*
    ppblt: 9999,
    ppbmt: 30,
    maxresults: 500,
    type: sampleTypeDataPreSelected,
    nullValues: false,
*/

export function queryAssayAnumberWFS(anumber) {
    const ecqlFilter = ` anumber = ${anumber}`

    return GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_au_soil_assays',
            maxFeatures: 99999,

            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            cql_filter: ecqlFilter
        }
    })

}

export function queryAssaySampleIdWFS(sampleId) {
    // Handle both numeric ID and string company sample ID
    const isNumeric = /^\d+$/.test(sampleId);
    let ecqlFilter;
    
    if (isNumeric) {
        // If input is numeric, search multiple possible ID fields
        // Try id, surfacesampleid, and companysampleid fields for numeric values
        ecqlFilter = `id = ${sampleId} OR surfacesampleid = ${sampleId} OR companysampleid = '${sampleId}' OR companysampleid LIKE '%${sampleId}%'`;
    } else {
        // If input contains non-numeric characters, only search companysampleid
        ecqlFilter = `companysampleid LIKE '%${sampleId}%'`;
    }

    console.log('Sample ID WFS Query Filter:', ecqlFilter); // Debug logging

    return GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_au_soil_assays',
            maxFeatures: 99999,
            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            cql_filter: ecqlFilter
        }
    })
}

/**
 * The layer the MAP renders from.
 *
 * 9,979,491 soil rows sit at only 7,264,826 distinct locations - 27% are the
 * same physical sample reported in more than one WAMEX submission (same
 * companysampleid, same value, a different anumber). Those are duplicate DOTS,
 * and the one drawn last wins, so a weak duplicate can sit on top of an
 * anomaly.
 *
 * `..._display` is a materialised view holding one row per location carrying
 * the HIGHEST au_ppb there, plus `samples_here`. The base table is untouched
 * and stays authoritative: A-Number search must still find a sample under
 * every report it appears in, and soil corrections are keyed on
 * (anumber, company_sample_id).
 *
 * Set to SOIL_LAYER_BASE to fall straight back to the old behaviour.
 */
export const SOIL_LAYER_BASE = 'ne:wa_au_soil_assays';
export const SOIL_LAYER_DISPLAY = 'ne:wa_au_soil_assays_display';
export const SOIL_MAP_LAYER = SOIL_LAYER_DISPLAY;

/**
 * Zoomed-out soils: a heatmap off a pre-aggregated grid.
 *
 * Below this zoom the WFS points do not render at all - `minzoom: 10.6` on
 * every circle layer - so zooming out has always just emptied the map. It could
 * not do anything else: 7.26M points cannot be drawn or fetched at state scale,
 * and thresholding by grade to make them fit shows the top 1.7% of locations
 * while implying the other 98.3% are barren.
 *
 * `ne:wa_au_soil_assays_heat` answers a different question - how much is here
 * and how hot - which every sample can contribute to. It is a grid holding two
 * resolutions (~1.1 km and ~11 km), each covering all 6,961,432 non-null
 * locations; the server picks one by zoom, so a tile is never more than ~266 KB.
 *
 * The handover is at 10.6 so it lines up exactly with where the points appear.
 */
/**
 * Base URL for tile templates below. Declared here because both
 * `soilTileUrl` and `soilHeatTileUrl` read it at module scope - a `const`
 * used before its declaration is a TDZ crash on import, not a hoisted
 * undefined, so this has to stay above both.
 */
const geoserverBase = (process.env.REACT_APP_GOLD_FINDER_GEOSERVER || '').replace(/\/$/, '');

/**
 * Individual soil samples, served as vector tiles.
 *
 * Replaces a per-viewport WFS GetFeature that returned the whole visible set as
 * GeoJSON. Tiles are cached by GWC and by the browser, so panning re-fetches
 * almost nothing, and the payload is a fraction of the JSON equivalent.
 *
 * WHY POINTS START AT 11.3 AND NOT 10.6.
 *
 * The tile layer thins by grade at low zoom - it has to, an unfiltered z10 tile
 * is 1.9 MB - so a tile only contains everything from z11 up. Measured against
 * prod: at display zoom 11.0 and below the lowest au_ppb present is 30, and at
 * 11.2 it is 0. Drawing points before that would mean a user who lowers the
 * threshold under 30 ppb silently sees nothing new until they zoom further in,
 * which is the failure this whole layer exists to avoid.
 *
 * So points begin where the data behind them is complete, and the heatmap -
 * which represents every sample, not a threshold slice - covers everything
 * below. That is a better answer at those zooms than thresholded points were.
 */
export const SOIL_TILE_LAYER = 'ne:wa_au_soil_assays_tiles';
export const SOIL_TILE_SOURCE = 'soil-points';
export const SOIL_TILE_SOURCE_LAYER = 'wa_au_soil_assays_tiles';
/** Click target: a wide invisible circle under the coloured bands. */
export const SOIL_TILE_BACKGROUND_LAYER = 'assay-tile-background';

export const soilTileUrl =
    `${geoserverBase}/gwc/service/tms/1.0.0/` +
    `${encodeURIComponent(SOIL_TILE_LAYER)}@EPSG%3A900913@pbf/{z}/{x}/{y}.pbf`;

/**
 * au_ppb as a number.
 *
 * GeoServer encodes a Postgres `numeric` column into a vector tile as a
 * STRING, so `au_ppb` arrives as "351". Comparing it unconverted is not an
 * error - it silently compares lexicographically, where "100" < "30" - so the
 * colour bands and thresholds would be quietly wrong rather than broken.
 */
const PPB = ['to-number', ['get', 'au_ppb']];

/**
 * The client-side equivalent of the CQL the WFS query used to send.
 *
 * With tiles the server no longer applies the user's filters - the tile is
 * fixed - so every filter has to be expressible against the attributes the
 * tile carries. That is why `gswa_load_date` had to be added to the display
 * view: a filter on an attribute the tile does not have cannot be honoured at
 * all.
 *
 * Dates compare as STRINGS. They arrive as "2021-08-27 14:10:56.737", which is
 * lexicographically ordered the same as chronologically, so a "YYYY-MM-DD"
 * prefix comparison is exact and needs no date parsing (which MapLibre
 * expressions cannot do anyway).
 */
export function buildSoilTileFilter(assayFilters) {
    const clauses = ['all'];

    // Threshold range. `has` guards the null case in both directions: without
    // it a missing au_ppb converts to 0 and would pass any `>= 0` bound.
    const inRange = ['all', ['>=', PPB, assayFilters.ppbmt], ['<=', PPB, assayFilters.ppblt]];
    clauses.push(assayFilters.nullValues
        ? ['any', ['!', ['has', 'au_ppb']], inRange]
        : ['all', ['has', 'au_ppb'], inRange]);

    const types = (assayFilters.type || []).map(t => t.value);
    // An empty selection means nothing is selected, not everything.
    clauses.push(types.length
        ? ['in', ['get', 'surfacesampletype'], ['literal', types]]
        : false);

    if (assayFilters.dateFilterEnabled && (assayFilters.dateFrom || assayFilters.dateTo)) {
        const range = ['all'];
        if (assayFilters.dateFrom) {
            range.push(['>=', ['get', 'gswa_load_date'], `${assayFilters.dateFrom}-01`]);
        }
        if (assayFilters.dateTo) {
            // Exclusive upper bound at the first day of the NEXT month, so the
            // whole of dateTo's month is included - same convention the CQL
            // used.
            const [y, m] = assayFilters.dateTo.split('-').map(Number);
            const next = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
            range.push(['<', ['get', 'gswa_load_date'], next]);
        }
        // Undated samples are never dropped by enabling the filter.
        clauses.push(['any', ['!', ['has', 'gswa_load_date']], range]);
    }

    return clauses;
}

export const SOIL_HEAT_LAYER = 'ne:wa_au_soil_assays_heat';
export const SOIL_HEAT_SOURCE = 'soil-heat';
export const SOIL_POINT_MINZOOM = 11.3;

/**
 * Where the heatmap STARTS fading out. It is fully gone by SOIL_POINT_MINZOOM,
 * so the heatmap and the points are never on screen together.
 *
 * THIS REPLACED A DELIBERATE OVERLAP, so read before widening it again. The
 * heatmap used to hold full strength to SOIL_POINT_MINZOOM and fade out
 * underneath the points afterwards, which was itself a fix: an earlier version
 * ramped down from about z10, so the map thinned to almost nothing before the
 * points appeared and then repopulated, and crossing the threshold also fetches
 * the points, widening the hole by a few hundred ms.
 *
 * The overlap cured that but created a worse problem. For the whole band a
 * sample was drawn twice - as a point, and as the smear it contributes to - and
 * the heatmap is aggregated server-side, so it CANNOT honour the user's
 * filters. A sample excluded by the threshold kept tinting the map underneath
 * the points that correctly excluded it.
 *
 * So the handover is clean again, but the fade is now SHORT rather than long:
 * full strength until 11.0, gone by 11.3. That is what keeps the original gap
 * from coming back - the old version's problem was a fade that began a whole
 * zoom level early, not the handover itself.
 */
export const SOIL_HEAT_FADE_START = 11.0;


export const soilHeatTileUrl =
    `${geoserverBase}/gwc/service/tms/1.0.0/` +
    `${encodeURIComponent(SOIL_HEAT_LAYER)}@EPSG%3A900913@pbf/{z}/{x}/{y}.pbf`;

/**
 * Sample type -> the heat grid's per-type max column.
 *
 * The grid carries one max_au_ppb column per sample type so the heatmap can
 * honour the type filter exactly rather than ignoring it. Keys must match the
 * `surfacesampletype` values the filter emits; a type missing from this map
 * would be silently unfilterable, which is why the view has a check that the
 * per-type columns reconstruct the overall max exactly.
 */
const HEAT_TYPE_COLUMN = {
    SOIL: 'p_soil',
    SHALLOWSURFDRILL: 'p_shallowsurfdrill',
    ROCKCHIP: 'p_rockchip',
    UNKN: 'p_unkn',
    STREAMSED: 'p_streamsed',
    DRILLSPOIL: 'p_drillspoil',
    VEGETATION: 'p_vegetation',
    MINEDUMP: 'p_minedump',
    WATER: 'p_water',
};

/** 'YYYY-MM' -> the integer yyyymmdd the grid stores, at the 1st of the month. */
const monthToInt = (ym, monthOffset = 0) => {
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(Date.UTC(y, (m - 1) + monthOffset, 1));
    return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
};

/**
 * The highest au_ppb in a cell AMONG THE SELECTED SAMPLE TYPES.
 *
 * This is what makes the heatmap agree with the dots. Taking the cell's overall
 * max would ignore the type filter, so turning off SOIL would thin the points
 * and leave the heat exactly as it was.
 *
 * Absent columns coalesce to 0 rather than null: `max` over a null is not
 * meaningful, and a cell holding none of the selected types is excluded by the
 * `has` check in the filter rather than by its weight.
 */
function selectedTypeMax(assayFilters) {
    const cols = (assayFilters.type || [])
        .map(t => HEAT_TYPE_COLUMN[t.value])
        .filter(Boolean);
    if (!cols.length) return 0;
    if (cols.length === 1) return ['to-number', ['coalesce', ['get', cols[0]], 0]];
    return ['max', ...cols.map(c => ['to-number', ['coalesce', ['get', c], 0]])];
}

/**
 * Which cells the heatmap draws, honouring the same filters as the points.
 *
 * Exact for the grade threshold and the sample type, together: a cell is shown
 * precisely when it contains at least one sample of a selected type at or above
 * the threshold, because the per-type max IS that sample's value.
 *
 * The date range is OVER-INCLUSIVE by construction. The cell carries only the
 * bounds of its load dates, so a cell holding 2005 and 2020 samples and nothing
 * between still passes a 2010-2015 filter. It can show a cell with nothing in
 * range; it can never hide one that has something, which is the right direction
 * to err.
 *
 * The upper grade cap (ppblt) is deliberately not applied. A cell whose max is
 * 5000 may also hold samples at 50 - hiding it is wrong, and drawing it at
 * weight 5000 is also wrong, and the cell carries nothing that resolves which.
 */
export function buildSoilHeatFilter(assayFilters) {
    const cols = (assayFilters.type || [])
        .map(t => HEAT_TYPE_COLUMN[t.value])
        .filter(Boolean);
    // No types selected means nothing is selected, not everything.
    if (!cols.length) return false;

    const clauses = ['all'];
    // The cell must actually hold one of the selected types.
    clauses.push(cols.length === 1 ? ['has', cols[0]] : ['any', ...cols.map(c => ['has', c])]);
    clauses.push(['>=', selectedTypeMax(assayFilters), assayFilters.ppbmt]);

    if (assayFilters.dateFilterEnabled && (assayFilters.dateFrom || assayFilters.dateTo)) {
        // Ranges overlap iff the cell ends at or after the range start AND
        // begins before the range end.
        if (assayFilters.dateFrom) {
            clauses.push(['>=', ['to-number', ['coalesce', ['get', 'd_max'], 0]],
                          monthToInt(assayFilters.dateFrom)]);
        }
        if (assayFilters.dateTo) {
            clauses.push(['<', ['to-number', ['coalesce', ['get', 'd_min'], 0]],
                          monthToInt(assayFilters.dateTo, 1)]);
        }
    }
    return clauses;
}

/**
 * Heat weight is GRADE, not density.
 *
 * Weighting by cell count would make the map brightest where sampling was
 * densest, which tracks where crews have already worked rather than where the
 * gold is - the opposite of what a prospector is looking for. `max_au_ppb` is
 * the strongest result in the cell, so one good hit surrounded by weak ones
 * still shows.
 *
 * Breaks mirror the point palette (>0 blue, >20 orange, >50 red, >100 purple),
 * so a hot spot when zoomed out is the same colour as the dots you find when
 * you zoom into it.
 */
export const soilHeatmapPaint = {
    // `to-number` is not decoration. GeoServer encodes a Postgres `numeric`
    // column into a vector tile as a STRING, and `interpolate` rejects a
    // string - the whole weight expression fails and the layer renders
    // NOTHING, reported only as a console warning ("Expected value to be of
    // type number, but found string instead"), so the map is simply blank with
    // no obvious cause. The view now casts to double precision so the tile
    // carries real numbers, and this stays as a guard: any future numeric
    // column added to that grid would otherwise silently blank the heatmap
    // again.
    // Weight comes from selectedTypeMax via soilHeatmapPaintFor below, so the
    // heat reflects the same samples the dots do. This literal is the
    // all-types fallback and the shape the interpolation uses.
    'heatmap-weight': [
        'interpolate', ['linear'], ['to-number', ['coalesce', ['get', 'max_au_ppb'], 0]],
        0, 0,
        20, 0.25,
        50, 0.5,
        100, 0.8,
        500, 1,
    ],
    // Zoomed out every cell is sub-pixel and needs help to be visible at all;
    // zoomed in they are dense enough to carry themselves.
    'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 2, 1.4, 7, 1.0, 10, 0.7],
    // Radius has to stay comfortably ABOVE the cell pitch, or the aggregation
    // becomes visible as itself - a field of separate blobs instead of a
    // surface.
    //
    // The trap is that pitch DOUBLES every zoom level while a linear-in-zoom
    // ramp grows arithmetically, so radius falls behind at the top of each
    // band no matter how the endpoints are chosen. Pitch also RESETS at z7,
    // where the server switches from the 0.1 degree grid to the 0.01 degree
    // one - ten times finer.
    //
    // Cell pitch in screen px, and roughly double that between POPULATED
    // neighbours since most cells are empty:
    //
    //   z5  0.1deg   2.3    z6  4.6    z6.99  9.0   (coarse band tops out)
    //   z7  0.01deg  0.9    z9  3.6    z10.6 11.0   (fine band tops out)
    //
    // So the ramp climbs steeply to z6.99, drops hard at z7 as the grid gets
    // finer, then climbs again. Measured at Leonora across z4-z10.4: the
    // largest connected region holds 81-95% of painted pixels through the
    // coarse band and the fine band never approaches one blob per cell
    // (z9 merges 51,836 cells into 138 blobs).
    'heatmap-radius': ['interpolate', ['linear'], ['zoom'],
        2, 10, 4, 10, 5, 12, 6, 20, 6.99, 36,   // 0.1 degree grid
        7, 10, 8, 12, 9, 18, 10, 30, 10.6, 40], // 0.01 degree grid
    'heatmap-color': [
        'interpolate', ['linear'], ['heatmap-density'],
        0,    'rgba(0,0,0,0)',
        0.15, 'rgba(0,0,255,0.5)',
        0.4,  '#FFA500',
        0.65, '#FF0000',
        1,    '#800080',
    ],
    // Gone by the time the points appear - never shown alongside them.
    'heatmap-opacity': ['interpolate', ['linear'], ['zoom'],
        SOIL_HEAT_FADE_START, 0.85, SOIL_POINT_MINZOOM, 0],
};

/** NOT CALLED - see the note on AssayOverlayMapLibre below. */
export function queryAssayWFS(bboxString, setStateGeoJSON, assayFilters) {

    if (assayFilters.anumber !== 0) { return }

    const assayTypeArr = assayFilters.type.map(e => e.value)

    const ecqlFilter = `
        BBOX(geom,${bboxString})
        and 
        (
        au_ppb BETWEEN ${assayFilters.ppbmt} AND ${assayFilters.ppblt}
        ${assayFilters.nullValues ? 'OR au_ppb is null' : ''}
        )
        and
        surfacesampletype in ('${assayTypeArr.join(`','`)}')
        
        `


    GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: SOIL_MAP_LAYER,
            maxFeatures: 99999,

            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            //bbox: bboxString,
            //filter: filterQuery
            cql_filter: ecqlFilter
        }
    }).then((e) => {
        // filter tenemements

        setStateGeoJSON(e.data);
    });
}


/**
 * Full detail for one sample, from the authoritative layer.
 *
 * The map now draws from the deduplicated display view, whose rows carry only
 * what rendering needs - so `gswa_load_date` and the reported-issue flags are
 * absent from a clicked feature. Rather than widen the display view (and have
 * it drift every time the popup changes), the popup asks the base layer for
 * the row it already knows the id of. That is always current even if the view
 * is stale.
 *
 * Two things about this query are load-bearing:
 *
 * It filters on `surfacesampleid`, NOT `id`, even though the two hold the same
 * value on every one of the 9.98M rows. There is no index on `id`, so that
 * form seq-scans the whole table - measured 13 s through GeoServer, which is
 * not a popup. `surfacesampleid` has had a btree index all along that nothing
 * was using (idx_scan = 0); going through it is an index scan and needs no new
 * index built.
 *
 * maxFeatures 1 is safe despite `id` not being unique. 888,603 ids appear 2-3
 * times, but every one of those groups was verified to be byte-identical
 * across all attributes - an ingest artifact, not the same sample under a
 * second A-Number. (Multi-report duplication is a distinct thing: same
 * LOCATION, different anumber and companysampleid, which is what the display
 * view collapses and what `samples_here` counts.)
 */
export function queryAssayById(id) {
    return GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS', version: '1.1.0', request: 'GetFeature',
            typeNames: SOIL_LAYER_BASE, maxFeatures: 1,
            outputFormat: 'application/json', srs: 'EPSG:4326',
            cql_filter: `surfacesampleid = ${Number(id)}`,
        },
    });
}

/**
 * Has this sample (or its whole A-Number) been reported as wrong?
 *
 * These arrive over WFS as JSON `null` when nothing is reported, and the checks
 * here used to be `!== undefined`. `null !== undefined` is true, so the warning
 * icon rendered on EVERY sample while the Report Sample button - gated on
 * `=== undefined` - never rendered at all. Both flags are ids, so presence is
 * the question, and `!= null` is the one comparison that answers it for null
 * and undefined alike.
 */
const isReported = (v) => v != null;

export function createSingleAssayPopupTable(assay, lngLat, setClosed, setModalShow, setReportedAssay) {
    return <Popup latitude={lngLat.lat} longitude={lngLat.lng}
        anchor="bottom"
        onClose={() => setClosed(false)}>
            <Table striped bordered  className="mb-1" >
                <tbody>
                    <tr>
                        <td>ID:</td>
                        <td>
                            {assay.properties.id}
                            {(isReported(assay.properties.reported_sample_id) ?
                                <FontAwesomeIcon title="This Sample Has been reported as inaccurate or incorrect." style={{ marginLeft: 3 }} icon={faCircleExclamation} />
                                : "")}
                        </td>
                    </tr>
                    <tr>
                        <td>Au PPB:</td>
                        <td>{assay.properties.au_ppb}</td>
                    </tr>
                    <tr>
                        <td>Sample Type:</td>
                        <td>{assay.properties.surfacesampletype}</td>
                    </tr>
                    <tr>
                        <td>A Number:</td>
                        <td>
                            <a target="_blank" href={`https://wamex.dmp.wa.gov.au/Wamex/Search/ReportDetails?ANumber=${assay.properties.anumber}`}>
                                {assay.properties.anumber}
                            </a>
                            {(isReported(assay.properties.reported_sample_anumber_id) ?
                                <FontAwesomeIcon title="The samples related to this A Number have been reported as inaccurate or incorrect." style={{ marginLeft: 3 }} icon={faCircleExclamation} />
                                : "")}
                        </td>
                    </tr>
                    <tr>
                        <td>Company Sample ID:</td>
                        <td>{assay.properties.companysampleid}</td>
                    </tr>
                    <tr>
                        <td>DMPE Load Date:</td>
                        <td>{formatLoadDate(assay.properties.gswa_load_date)}</td>
                    </tr>
                    {/*
                      * The map draws one dot per location, so say when a dot is
                      * standing in for more than one sample - otherwise the
                      * other reports at this spot are invisible with nothing
                      * hinting they exist. The value shown is the highest Au
                      * here, which is what the dot is coloured by.
                      */}
                    {assay.properties.samples_here > 1 ? (
                        <tr>
                            <td>Samples here:</td>
                            <td title="More than one report recorded a sample at this exact location. The highest Au value is shown.">
                                {assay.properties.samples_here} (showing highest Au)
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </Table>
            {
                (
                    !isReported(assay.properties.reported_sample_anumber_id) && !isReported(assay.properties.reported_sample_id) ?
                        <button className="btn btn-secondary btn-sm" style={{fontSize:10}} onClick={() => { setModalShow(true); setReportedAssay(assay.properties) }}>Report Sample</button>
                        : ""
                )
            }
    </Popup >
}

function AssayLayers({ assayMarkerColourSettings, overlayVisibility }) {

    const orderedAssayMarkerArray = assayMarkerColourSettings.toSorted((a, b) => a.moreThan - b.moreThan);

    const rows = [];

    // push  white under
    for (let i = 0; i < orderedAssayMarkerArray.length; i++) {
        const next = i + 1;

        const layerConfig = {
            id: orderedAssayMarkerArray[i].id,
            'source': 'soilsamples',
            type: 'circle',
            // type:'symbol',
            minzoom: 10.6,
            paint: { 'circle-color': orderedAssayMarkerArray[i].color, 'circle-radius': 6 },
            filter: ['all',
                ['>=', 'au_ppb', orderedAssayMarkerArray[i].moreThan],
                ['<', 'au_ppb', i !== orderedAssayMarkerArray.length - 1 ? orderedAssayMarkerArray[next].moreThan : 9999],
                //['!=', 'au_ppb', null]
            ],
            layout: { visibility: `${overlayVisibility.soils ? "visible" : "none"}` }
            //layout:{'icon-image':'test-img'}
        }

        rows.push(<Layer {...layerConfig} />)
    }
    return rows;
}


/**
 * NOT MOUNTED. The map renders AssayOverlayMapLibreOptimized from
 * AssayOverlayOptimized.jsx; this component and `queryAssayWFS` above are
 * referenced nowhere. Only `createSingleAssayPopupTable` and the heatmap
 * constants in this file are live.
 *
 * Left in place as the unoptimised reference implementation, but edit it
 * expecting NO effect on the map - a change to the soil layer here silently
 * does nothing, which has already cost one round of debugging.
 */
export function AssayOverlayMapLibre({ overlayVisibility, assayMarkerColourSettings, assayGeoJson, setAssayGeojson, assayFilters }) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "assay_samples",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4326" } },
        "features": []
    };

    const { goldfindermap } = useMap(); // `mymap` is the id in <Map id="mymap" />

    useEffect(() => {

        if (!goldfindermap) return undefined;

        if (goldfindermap.getZoom() > 10.6) {
            const bounds = goldfindermap.getBounds();
            var wfsbbox = [bounds.getSouthWest().lng, bounds.getSouthWest().lat, bounds.getNorthEast().lng, bounds.getNorthEast().lat].join();
            queryAssayWFS(wfsbbox, setAssayGeojson, assayFilters)
        }

    }, [assayFilters])


    const backgroundConfig = {
        id: 'assay-background-config',
        'source': 'soilsamples',
        minzoom: 10.6,
        type: 'circle',
        paint: { 'circle-color': '#f9f9f9', 'circle-radius': 7 },
        layout: { visibility: `${overlayVisibility.soils ? "visible" : "none"}` }
    }

    const heatConfig = {
        id: 'soil-heat-layer',
        source: SOIL_HEAT_SOURCE,
        'source-layer': 'wa_au_soil_assays_heat',
        type: 'heatmap',
        maxzoom: SOIL_POINT_MINZOOM,
        paint: soilHeatmapPaint,
        layout: { visibility: `${overlayVisibility.soils ? "visible" : "none"}` }
    }

    return <>
        {/*
          * Tiles are served straight off GeoServer's GWC rather than through
          * the WFS helper, so this needs no bbox tracking, no debounce and no
          * abort controller - MapLibre asks for the tiles the viewport covers
          * and caches them itself. The grid is pre-aggregated, so panning at
          * low zoom costs nothing on the database.
          */}
        <Source id={SOIL_HEAT_SOURCE} type="vector" scheme="tms" tiles={[soilHeatTileUrl]} minzoom={1} maxzoom={11}>
            <Layer {...heatConfig} />
        </Source>
        <Source id="soilsamples" type="geojson" data={assayGeoJson}>
            <Layer {...backgroundConfig} />
            <AssayLayers overlayVisibility={overlayVisibility} assayMarkerColourSettings={assayMarkerColourSettings} />
        </Source>
    </>
}

/**
 * The heat paint for a given filter state.
 *
 * Only the weight varies: it reads the max among the SELECTED sample types
 * rather than the cell's overall max, so the heat and the dots are describing
 * the same set of samples. Everything else - radius, intensity, colour ramp,
 * the crossfade - is fixed and shared.
 */
export function soilHeatmapPaintFor(assayFilters) {
    return {
        ...soilHeatmapPaint,
        'heatmap-weight': [
            'interpolate', ['linear'], selectedTypeMax(assayFilters),
            0, 0,
            20, 0.25,
            50, 0.5,
            100, 0.8,
            500, 1,
        ],
    };
}
